<template>
  <div>
    <eg-b-sidebar
      id="source-material-sidebar"
      ref="sidebarRef"
      width="680px"
      right
      shadow
      lazy
      no-header
      backdrop
      backdrop-variant="transparent"
      sidebar-class="bg-white"
      :class="['source-materials-sidebar']"
      style="z-index: var(--layout-z-index-sidebar)"
      @shown="onSidebarShown"
    >
      <template #default="{ hide }">
        <div class="d-flex flex-column h-100">
          <eg-modal-header
            closable
            title="Reference Material Library"
            @onClose="hide"
          >
            <eg-button
              type="secondary"
              icon="plus-add-more-maximize"
              @click="openCreateSourceMaterialModal"
            >
              {{ $t("materials.createEditModal.createNewMaterial") }}
            </eg-button>
          </eg-modal-header>
          <div class="p-3 flex-grow-1 source-materials-sidebar-content">
            <div
              v-if="selectedPlatform || allSourceMaterials.length !== 0"
              class="py-3 d-flex justify-content-start flex-gap-1"
            >
              <platform-dropdown
                :value="selectedPlatform"
                @input="onSelectPlatform"
              />
              <eg-search
                :placeholder="$t('common.search')"
                @onChange="onSearchUpdate"
              />
            </div>
            <div
              v-if="allSourceMaterials.length === 0"
              class="d-flex justify-content-center w-100 h-100"
            >
              <empty-state
                :title="$t('sourceMaterials.noMaterialYet')"
                :description="$t('sourceMaterials.noMaterialDescription')"
              />
            </div>
            <div v-else class="eg-table-wrapper">
              <div class="d-flex flex-column overflow-hidden flex-gap-1">
                <source-material-item
                  v-for="item in allSourceMaterials"
                  :key="item.id"
                  :item="item"
                  @deleted="fetchSourceMaterials"
                  @saved="fetchSourceMaterials"
                  @select="onSelectHandler"
                >
                  <template #extra-actions>
                    <eg-button
                      size="small"
                      type="primary"
                      @click="() => onSelectHandler(item)"
                    >
                      {{ $t("common.upload") }}
                    </eg-button>
                  </template>
                </source-material-item>
              </div>
            </div>
          </div>
        </div>
      </template>
    </eg-b-sidebar>
  </div>
</template>

<script>
import { getSourceMaterials } from "@/api";
import debounce from "@/utils/debounce";
import deleteConfirmationModalMixin from "@/mixins/deleteConfirmationModal";
import EgModalHeader from "@/components/global/eg/modals/eg_modal_header.vue";
import EgSearch from "@/components/global/eg-search/eg_search.vue";
import EmptyState from "@/components/global/empty_state.vue";
import CreateEditSourceMaterialModal from "@/components/source-materials/create_edit_source_material_modal.vue";
import PlatformDropdown from "@/components/source-materials/platform-dropdown.vue";
import SourceMaterialItem from "@/components/source-materials/source_material_item.vue";

export default {
  name: "SourceMaterialSidebar",

  components: {
    PlatformDropdown,
    EmptyState,
    EgSearch,
    EgModalHeader,
    SourceMaterialItem,
  },

  mixins: [deleteConfirmationModalMixin],

  props: {
    value: {
      type: Object,
      default: () => {},
    },
    additionalSourceMaterials: {
      type: Array,
      default: () => [],
    },
    // TODO: remove this after refactoring
    // showAddNew: Boolean,
    // autoCommit: {
    //   type: Boolean,
    //   default: true,
    // },
  },

  emits: ["selectSourceMaterial"],

  data() {
    return {
      debouncedSearchText: "",
      selectedPlatform: null,
      sourceMaterials: this.additionalSourceMaterials,
      loading: true,
      count: 0,
      limit: null,
      currentPage: 1,
    };
  },

  computed: {
    allSourceMaterials() {
      return [
        ...(this.additionalSourceMaterials || []),
        ...(this.sourceMaterials || []),
      ];
    },
  },

  mounted() {
    this.fetchSourceMaterials();
  },

  methods: {
    hideSideBar() {
      this.$refs.sidebarRef.hide("source-material-sidebar");
    },

    openCreateSourceMaterialModal() {
      this.$egModal.show("create-edit-source-material-modal", {
        component: CreateEditSourceMaterialModal,
        props: {
          sourceMaterialId: null,
        },
        on: {
          onSave: () => {
            this.fetchSourceMaterials();
          },
        },
      });
    },

    onSidebarShown() {
      this.debouncedSearchText = "";
      this.fetchSourceMaterials();
    },

    onSearchUpdate: debounce(function (value = "") {
      if (
        value.length >= 2 ||
        (this.debouncedSearchText.length >= 2 && value.length === 0)
      ) {
        this.debouncedSearchText = value;
        this.currentPage = 1;
        this.fetchSourceMaterials();
      }
    }, 500),

    async fetchSourceMaterials() {
      const params = {
        page: this.currentPage,
        limit: 1000,
      };

      if (this.debouncedSearchText) {
        params.title = this.debouncedSearchText;
      }

      if (this.selectedPlatform) {
        params.type = this.selectedPlatform?.key;
      }

      try {
        this.loading = true;
        const { data } = await getSourceMaterials(params);
        this.sourceMaterials = data.rows;
        this.limit = data.limit;
        this.count = data.count;

        this.loading = false;
        this.error = false;
      } catch (error) {
        this.error = error.response?.data?.error;
        this.loading = false;
      }
    },

    onSelectHandler(item) {
      this.$emit("selectSourceMaterial", item);
      this.hideSideBar();
    },

    onSelectPlatform(platform) {
      this.selectedPlatform = platform ? platform : null;
      this.fetchSourceMaterials();
    },
  },
};
</script>

<style lang="scss" scoped>
.source-materials-sidebar {
  .source-materials-sidebar-content {
    background-color: var(--color-surface-neutral-container-1);
  }
}
</style>
